@file:OptIn(ExperimentalMaterial3Api::class)

package com.example.monetix.ui.dialogs

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.example.monetix.ThemeMode
import com.example.monetix.ui.components.MonetixGlassSwitch
import com.example.monetix.utils.biometricAuth
import com.example.monetix.utils.MediaHandler

// ==========================================
// 🚀 COMPONENTI GLASSMORPHIC REVOLUT
// ==========================================

@Composable
private fun SectionTitle(title: String) {
    Text(
        text = title.uppercase(),
        style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold, letterSpacing = 1.sp),
        color = Color(0xFF60A5FA),
        modifier = Modifier.padding(start = 8.dp, bottom = 8.dp)
    )
}

@Composable
private fun SettingsPanel(content: @Composable ColumnScope.() -> Unit) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = Color.White.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            content = content
        )
    }
}

@Composable
private fun SettingsInputItem(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    keyboardType: KeyboardType = KeyboardType.Text
) {
    var localText by remember { mutableStateOf(value) }
    LaunchedEffect(value) {
        if (value != localText) localText = value
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 16.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(text = label, style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.SemiBold), color = Color.White, modifier = Modifier.weight(1f))
        BasicTextField(
            value = localText,
            onValueChange = {
                if (keyboardType == KeyboardType.Number) {
                    if (it.all { c -> c.isDigit() || c == ',' || c == '.' }) {
                        localText = it
                        onValueChange(it)
                    }
                } else {
                    localText = it
                    onValueChange(it)
                }
            },
            textStyle = LocalTextStyle.current.copy(color = Color(0xFF60A5FA), fontSize = 16.sp, fontWeight = FontWeight.Bold, textAlign = TextAlign.End),
            keyboardOptions = KeyboardOptions(keyboardType = keyboardType, autoCorrectEnabled = false),
            cursorBrush = SolidColor(Color.White),
            modifier = Modifier.weight(1f),
            singleLine = true
        )
    }
}

@Composable
private fun SettingsToggleItem(
    title: String,
    subtitle: String? = null,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onCheckedChange(!checked) }
            .padding(horizontal = 16.dp, vertical = 14.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.weight(1f).padding(end = 16.dp)) {
            Text(text = title, style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.SemiBold), color = Color.White)
            if (subtitle != null) {
                Spacer(modifier = Modifier.height(2.dp))
                Text(text = subtitle, style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
            }
        }
        MonetixGlassSwitch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            activeColor = Color(0xFF60A5FA)
        )
    }
}

@Composable
private fun SettingsActionItem(
    title: String,
    value: String,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 16.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(text = title, style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.SemiBold), color = Color.White)
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(text = value, style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA))
            Spacer(modifier = Modifier.width(4.dp))
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null, tint = Color.White.copy(alpha = 0.5f))
        }
    }
}

// ==========================================
// 🚀 DIALOG PRINCIPALE
// ==========================================

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsDialog(
    userName: String,
    salaryDay: Int,
    decemberSalaryDay: Int,
    anticipateWeekends: Boolean,
    hideBalances: Boolean,

    deadlineHour: Int,
    deadlineMinute: Int,
    deadlineEnabled: Boolean,

    summaryHour: Int,
    summaryMinute: Int,
    summaryEnabled: Boolean,

    themeMode: ThemeMode,
    onThemeModeChange: (ThemeMode) -> Unit,
    onUserNameChange: (String) -> Unit,
    onSalaryDayChange: (Int) -> Unit,
    onDecemberSalaryDayChange: (Int) -> Unit,
    appBackgroundUri: String?,
    onSaveAppBackgroundUri: (String?) -> Unit,
    onAnticipateWeekendsChange: (Boolean) -> Unit,
    onHideBalancesChange: (Boolean) -> Unit,
    appLockEnabled: Boolean,
    onAppLockEnabledChange: (Boolean) -> Unit,
    backupFolderUri: String?,
    lastBackupEpochMs: Long,
    onPickBackupFolder: () -> Unit,
    onQuickBackupToFolder: () -> Unit,
    onBackupRequest: () -> Unit,
    onRestoreRequest: () -> Unit,

    onDeadlineSettingsChange: (Int, Int, Boolean) -> Unit,
    onSummarySettingsChange: (Int, Int, Boolean) -> Unit,

    onDismiss: () -> Unit
) {
    var nameText by remember(userName) { mutableStateOf(userName) }
    var salaryText by remember(salaryDay) { mutableStateOf(salaryDay.toString()) }
    var decSalaryText by remember(decemberSalaryDay) { mutableStateOf(decemberSalaryDay.toString()) }

    var deadlineEnabledLocal by remember { mutableStateOf(deadlineEnabled) }
    var deadlineHourLocal by remember { mutableStateOf(deadlineHour) }
    var deadlineMinuteLocal by remember { mutableStateOf(deadlineMinute) }
    var showDeadlineTimePicker by remember { mutableStateOf(false) }

    var summaryEnabledLocal by remember { mutableStateOf(summaryEnabled) }
    var summaryHourLocal by remember { mutableStateOf(summaryHour) }
    var summaryMinuteLocal by remember { mutableStateOf(summaryMinute) }
    var showSummaryTimePicker by remember { mutableStateOf(false) }

    val handleSaveAndDismiss = {
        salaryText.trim().toIntOrNull()?.let { onSalaryDayChange(it.coerceIn(1, 31)) }
        decSalaryText.trim().toIntOrNull()?.let { onDecemberSalaryDayChange(it.coerceIn(1, 31)) }
        onUserNameChange(nameText)
        onDeadlineSettingsChange(deadlineHourLocal, deadlineMinuteLocal, deadlineEnabledLocal)
        onSummarySettingsChange(summaryHourLocal, summaryMinuteLocal, summaryEnabledLocal)
        onDismiss()
    }

    val infiniteTransition = rememberInfiniteTransition()
    val bgOffset by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 1000f,
        animationSpec = infiniteRepeatable(
            animation = tween(20000, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "bg_offset"
    )

    Dialog(
        onDismissRequest = handleSaveAndDismiss,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnClickOutside = false,
            dismissOnBackPress = true
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            Color(0xFF4F46E5).copy(alpha = 0.8f),
                            Color(0xFF1E1B4B),
                            Color(0xFF0F172A)
                        ),
                        startY = 0f,
                        endY = bgOffset + 1500f
                    )
                )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .systemBarsPadding()
                    .imePadding()
            ) {
                // Top header with back button
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(
                        onClick = handleSaveAndDismiss,
                        modifier = Modifier
                            .size(40.dp)
                            .background(Color.White.copy(alpha = 0.1f), CircleShape)
                    ) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Indietro", tint = Color.White)
                    }
                }

                Text(
                    text = "Impostazioni",
                    style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                    color = Color.White,
                    modifier = Modifier.padding(horizontal = 24.dp, vertical = 8.dp)
                )

                // Scrollable Settings Content
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = 16.dp, vertical = 16.dp),
                    verticalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    Column {
                        SectionTitle("Profilo")
                        SettingsPanel {
                            SettingsInputItem(
                                label = "Il tuo nome",
                                value = nameText,
                                onValueChange = { nameText = it }
                            )
                        }
                    }

                    Column {
                        SectionTitle("Ciclo Contabile e Stipendio")
                        SettingsPanel {
                            SettingsInputItem(
                                label = "Mesi Standard (1-31)",
                                value = salaryText,
                                onValueChange = { salaryText = it.take(2) },
                                keyboardType = KeyboardType.Number
                            )
                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), thickness = 1.dp, modifier = Modifier.padding(start = 16.dp))
                            SettingsInputItem(
                                label = "Dicembre (1-31)",
                                value = decSalaryText,
                                onValueChange = { decSalaryText = it.take(2) },
                                keyboardType = KeyboardType.Number
                            )
                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), thickness = 1.dp, modifier = Modifier.padding(start = 16.dp))
                            SettingsToggleItem(
                                title = "Anticipa per i Weekend",
                                subtitle = "Sposta la data al venerdì se cade di sabato o domenica.",
                                checked = anticipateWeekends,
                                onCheckedChange = onAnticipateWeekendsChange
                            )
                        }
                    }

                    Column {
                        SectionTitle("Privacy & Sicurezza")
                        SettingsPanel {
                            SettingsToggleItem(
                                title = "Oscura importi",
                                subtitle = "Nasconde i saldi all'avvio",
                                checked = hideBalances,
                                onCheckedChange = onHideBalancesChange
                            )
                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), thickness = 1.dp, modifier = Modifier.padding(start = 16.dp))
                            SettingsToggleItem(
                                title = "Protezione biometrica",
                                subtitle = "Richiedi impronta o codice all'avvio",
                                checked = appLockEnabled,
                                onCheckedChange = { enabled ->
                                    if (enabled) {
                                        val bio = biometricAuth
                                        if (bio != null && bio.isAvailable()) {
                                            bio.authenticate(
                                                title = "Conferma Biometria",
                                                subtitle = "Verifica la tua identità per abilitare la protezione",
                                                onSuccess = { onAppLockEnabledChange(true) },
                                                onError = { onAppLockEnabledChange(false) }
                                            )
                                        } else {
                                            onAppLockEnabledChange(true)
                                        }
                                    } else {
                                        onAppLockEnabledChange(false)
                                    }
                                }
                            )
                        }
                    }

                    Column {
                        SectionTitle("Notifiche e Promemoria")
                        SettingsPanel {
                            SettingsToggleItem(
                                title = "Avviso Scadenze",
                                subtitle = "Ti avvisa se oggi hai rate da pagare",
                                checked = deadlineEnabledLocal,
                                onCheckedChange = { deadlineEnabledLocal = it }
                            )
                            AnimatedVisibility(visible = deadlineEnabledLocal) {
                                Column {
                                    HorizontalDivider(color = Color.White.copy(alpha = 0.1f), thickness = 1.dp, modifier = Modifier.padding(start = 16.dp))
                                    SettingsActionItem(
                                        title = "Orario avviso scadenze",
                                        value = "${deadlineHourLocal.toString().padStart(2, '0')}:${deadlineMinuteLocal.toString().padStart(2, '0')}",
                                        onClick = { showDeadlineTimePicker = true }
                                    )
                                }
                            }

                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), thickness = 1.dp, modifier = Modifier.padding(start = 16.dp))

                            SettingsToggleItem(
                                title = "Resoconto Serale",
                                subtitle = "Ti ricorda di registrare le spese quotidiane",
                                checked = summaryEnabledLocal,
                                onCheckedChange = { summaryEnabledLocal = it }
                            )
                            AnimatedVisibility(visible = summaryEnabledLocal) {
                                Column {
                                    HorizontalDivider(color = Color.White.copy(alpha = 0.1f), thickness = 1.dp, modifier = Modifier.padding(start = 16.dp))
                                    SettingsActionItem(
                                        title = "Orario resoconto",
                                        value = "${summaryHourLocal.toString().padStart(2, '0')}:${summaryMinuteLocal.toString().padStart(2, '0')}",
                                        onClick = { showSummaryTimePicker = true }
                                    )
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(24.dp))
                    Text(text = "Personalizzazione Tema", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White, modifier = Modifier.padding(horizontal = 8.dp, vertical = 8.dp))
                    Surface(modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(16.dp)), color = Color.White.copy(alpha = 0.08f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))) {
                        Column {
                            val mediaHandler = com.example.monetix.utils.rememberMediaHandler(
                                onImageResult = { uri ->
                                    onSaveAppBackgroundUri(uri)
                                },
                                onVoiceResult = { _ -> }
                            )
                            
                            SettingsActionItem(
                                title = "Sfondo personalizzato",
                                value = if (appBackgroundUri != null) "Cambia Sfondo" else "Scegli Sfondo",
                                onClick = { mediaHandler.launchGallery() }
                            )

                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), thickness = 1.dp, modifier = Modifier.padding(start = 16.dp))
                            
                            SettingsActionItem(
                                title = "Rimuovi sfondo",
                                value = "Rimuovi",
                                onClick = { onSaveAppBackgroundUri(null) }
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(32.dp))
                }
            }

            // OROLOGIO PER SCADENZE
            if (showDeadlineTimePicker) {
                val timeState = rememberTimePickerState(initialHour = deadlineHourLocal, initialMinute = deadlineMinuteLocal, is24Hour = true)
                AlertDialog(
                    onDismissRequest = { showDeadlineTimePicker = false },
                    shape = RoundedCornerShape(28.dp),
                    containerColor = Color.White.copy(alpha = 0.12f),
                    title = { Text("Orario Scadenze", fontWeight = FontWeight.Bold, color = Color.White) },
                    text = { Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) { TimePicker(state = timeState) } },
                    confirmButton = {
                        Button(
                            shape = RoundedCornerShape(14.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White),
                            onClick = { deadlineHourLocal = timeState.hour; deadlineMinuteLocal = timeState.minute; showDeadlineTimePicker = false }
                        ) { Text("CONFERMA", fontWeight = FontWeight.Bold) }
                    },
                    dismissButton = {
                        TextButton(onClick = { showDeadlineTimePicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    }
                )
            }

            // OROLOGIO PER RESOCONTO
            if (showSummaryTimePicker) {
                val timeState = rememberTimePickerState(initialHour = summaryHourLocal, initialMinute = summaryMinuteLocal, is24Hour = true)
                AlertDialog(
                    onDismissRequest = { showSummaryTimePicker = false },
                    shape = RoundedCornerShape(28.dp),
                    containerColor = Color.White.copy(alpha = 0.12f),
                    title = { Text("Orario Resoconto", fontWeight = FontWeight.Bold, color = Color.White) },
                    text = { Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) { TimePicker(state = timeState) } },
                    confirmButton = {
                        Button(
                            shape = RoundedCornerShape(14.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White),
                            onClick = { summaryHourLocal = timeState.hour; summaryMinuteLocal = timeState.minute; showSummaryTimePicker = false }
                        ) { Text("CONFERMA", fontWeight = FontWeight.Bold) }
                    },
                    dismissButton = {
                        TextButton(onClick = { showSummaryTimePicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    }
                )
            }
        }
    }
}